// Shared types + colors for the Home feed cards.
// Card union, color palette, and typography constants live here so each
// individual card component can be small and stylistically consistent.

import type { MemoryWithChildren } from "@/hooks/useMemories";
import type { ContentLibraryItem } from "@/hooks/useContentLibrary";
import type { SparkQuestion } from "@/hooks/useSparkQuestion";
import type { CardContext } from "@/types/cardContext";

export interface CollectionCardData {
  collectionId: string;
  collectionName: string;
  collectionIcon: string;
  collectionColor: string;
  entryCount: number;
  lastEntryAt: string | null;
  contentLibraryId: string;
  cardContext: CardContext;
  /** Adult connection whitelist carried through from the collection row. */
  participantConnectionIds: string[];
  /** Child IDs the collection primarily concerns. */
  participantChildIds: string[];
  /** Permanently locked child IDs — forwarded to CollectionDetailSheet. */
  lockedChildIds: string[];
  /** Permanently locked member IDs — forwarded to CollectionDetailSheet. */
  lockedMemberIds: string[];
}

/**
 * Personal Mori-message card instance, spawned by moriMessageSubscriber
 * when a lifecycle event fires. Title/body are already interpolated from
 * `payload`. A proper renderer will land in a follow-up prompt —
 * HomeFeed currently renders null for this type as a placeholder.
 */
export interface MoriMessageCardData {
  instanceId: string;
  contentLibraryId: string;
  title: string | null;
  body: string;
  ctaAction: string;
  cardType: string;
}

export type FeedCard =
  | { type: "streak"; data: null }
  | { type: "memory"; data: MemoryWithChildren }
  | { type: "game"; data: ContentLibraryItem }
  | { type: "tip"; data: ContentLibraryItem }
  | { type: "spark"; data: SparkQuestion }
  | { type: "milestone"; data: ContentLibraryItem }
  | { type: "collection"; data: CollectionCardData }
  | { type: "mori_message"; data: MoriMessageCardData };

export interface CardColors {
  bg: string;
  border: string;
  title: string;
  body: string;
}

export const CARD_COLORS: Record<FeedCard["type"], CardColors> = {
  streak:       { bg: "#FAECE7", border: "#F0997B", title: "#712B13", body: "#993C1D" },
  memory:       { bg: "#E1F5EE", border: "#9FE1CB", title: "#085041", body: "#0F6E56" },
  game:         { bg: "#EEEDFE", border: "#AFA9EC", title: "#3C3489", body: "#534AB7" },
  tip:          { bg: "#FDF0EE", border: "#E8A99F", title: "#8B3A2E", body: "#C45B4B" },
  spark:        { bg: "#E6F1FB", border: "#85B7EB", title: "#0C447C", body: "#185FA5" },
  milestone:    { bg: "#EAF3DE", border: "#97C459", title: "#27500A", body: "#3B6D11" },
  collection:   { bg: "#F5F0FF", border: "#C4B8F0", title: "#3C3489", body: "#534AB7" },
  mori_message: { bg: "#FDF5E8", border: "#EBC98C", title: "#7A4A10", body: "#A56A20" },
};

export const CARD_RADIUS = 16;
export const CARD_PADDING = 18;

// Reusable inline-style helpers so each card stays visually consistent.
export const labelStyle = (color: string): React.CSSProperties => ({
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color,
  fontWeight: 600,
});

export const titleStyle = (color: string): React.CSSProperties => ({
  fontSize: 15,
  fontWeight: 500,
  color,
  lineHeight: 1.3,
});

export const bodyStyle = (color: string): React.CSSProperties => ({
  fontSize: 13,
  lineHeight: 1.5,
  color,
});

export const ctaStyle = (
  bg: string,
  color: string,
): React.CSSProperties => ({
  backgroundColor: bg,
  color,
  fontSize: 12,
  fontWeight: 500,
  padding: "6px 16px",
  borderRadius: 20,
  border: "none",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
});

/** Full-width pill CTA shown at the bottom of every non-streak card. */
export const fullCtaStyle = (
  bg: string,
  color: string,
): React.CSSProperties => ({
  backgroundColor: bg,
  color,
  fontSize: 14,
  fontWeight: 500,
  padding: "10px 0",
  borderRadius: 24,
  border: "none",
  cursor: "pointer",
  width: "100%",
  textAlign: "center",
  display: "block",
});

/** Responsive card shell — approx half the screen height with sensible bounds. */
export const cardShellStyle = (
  bg: string,
  border: string,
): React.CSSProperties => ({
  backgroundColor: bg,
  border: `1px solid ${border}`,
  height: "calc(50dvh - 60px)",
  minHeight: 240,
  maxHeight: 380,
  padding: CARD_PADDING,
  borderRadius: CARD_RADIUS,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
});
